import { Button } from '@heroui/react'
import {
  type BaseAdjustmentProps,
  computeContextState,
  computeIntent,
  type ContextState,
  type IntentKind,
  NO_CHANGE_TEXT,
  RESET_LABEL,
  resolveIntent,
} from '@zealth/core/adjustments'
import { ArrowDown, ArrowUp, RotateCcw } from 'lucide-react'
import { type ReactNode } from 'react'

import { Stepper } from '../Stepper'

const INTENT_CLASS: Record<IntentKind, string> = {
  success: 'text-success',
  danger: 'text-danger',
  muted: 'text-muted',
}

type AdjustmentContextProps = {
  state: ContextState
  formatDelta?: (diff: number, baseline: number) => ReactNode
  betterWhen?: 'lower' | 'higher'
  onReset?: () => void
}

const AdjustmentContext = ({
  state,
  formatDelta,
  betterWhen,
  onReset,
}: AdjustmentContextProps) => {
  if (state.kind === 'unchanged') {
    return (
      <div className="text-sm text-muted flex items-center min-h-9">
        {NO_CHANGE_TEXT}
      </div>
    )
  }
  const color =
    INTENT_CLASS[resolveIntent(computeIntent(state.delta, betterWhen))]
  return (
    <div className={`text-sm flex items-center gap-1 min-h-9 ${color}`}>
      {state.direction === 'up' ? (
        <ArrowUp className="size-4" />
      ) : (
        <ArrowDown className="size-4" />
      )}
      {formatDelta?.(state.delta, state.baseline)}
      {onReset && (
        <Button
          isIconOnly
          variant="ghost"
          size="sm"
          onPress={onReset}
          aria-label={RESET_LABEL}
        >
          <RotateCcw className="size-4 text-muted" />
        </Button>
      )}
    </div>
  )
}

/**
 * Web implementation of `BaseAdjustmentComponent`. Renders a Stepper card
 * with the sticker + label + value + delta+reset stack in the center and
 * minus / plus buttons on the sides.
 */
export const BaseAdjustment = ({
  Sticker,
  label,
  value,
  baseline,
  formatValue,
  formatDelta,
  betterWhen,
  onReset,
  onIncrement,
  onDecrement,
  onIncrementPressStart,
  onIncrementPressEnd,
  onDecrementPressStart,
  onDecrementPressEnd,
  isIncrementDisabled,
  isDecrementDisabled,
}: BaseAdjustmentProps) => {
  const formatted = formatValue(value)
  const state = computeContextState({ value, baseline })

  return (
    <Stepper
      onIncrement={onIncrement}
      onDecrement={onDecrement}
      onIncrementPressStart={onIncrementPressStart}
      onIncrementPressEnd={onIncrementPressEnd}
      onDecrementPressStart={onDecrementPressStart}
      onDecrementPressEnd={onDecrementPressEnd}
      isIncrementDisabled={isIncrementDisabled}
      isDecrementDisabled={isDecrementDisabled}
    >
      <div className="flex gap-4">
        <div className="flex items-center">
          <Sticker size={40} />
        </div>
        <div>
          <div className="text-xs uppercase tracking-wider font-medium text-muted">
            {label}
          </div>
          <div className="text-2xl font-semibold tracking-tight">
            {formatted}
          </div>
          <AdjustmentContext
            state={state}
            formatDelta={formatDelta}
            betterWhen={betterWhen}
            onReset={onReset}
          />
        </div>
      </div>
    </Stepper>
  )
}
